
import React, { useState } from "react";
import { Check, Loader2, Sparkles, Tag } from "lucide-react";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { SUBSCRIPTION_TIERS, type SubscriptionTier } from "@/config/subscriptionTiers";

interface SubscriptionPlansProps {
  currentTier?: SubscriptionTier | null;
  subscribed?: boolean;
}

interface PlanConfig {
  id: SubscriptionTier;
  product_id: string;
  price_id: string;
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  popular?: boolean;
}

const plans: PlanConfig[] = [
  {
    id: "monthly",
    ...SUBSCRIPTION_TIERS.monthly,
    description: "Perfect for those just starting their manifestation journey",
    features: [
      "Daily personalized affirmations",
      "Access to guided manifestation exercises",
      "Weekly progress tracking",
      "Basic visualization tools",
    ],
  },
  {
    id: "quarterly",
    ...SUBSCRIPTION_TIERS.quarterly,
    description: "Our most popular option for dedicated manifestors",
    features: [
      "All Monthly Plan features",
      "Advanced manifestation techniques",
      "Monthly manifestation challenges",
      "Priority support",
      "10% discount compared to monthly",
    ],
    popular: true,
  },
  {
    id: "yearly",
    ...SUBSCRIPTION_TIERS.yearly,
    description: "Best value for serious manifestation practitioners",
    features: [
      "All Quarterly Plan features",
      "Exclusive manifestation workshops",
      "1-on-1 coaching session quarterly",
      "Advanced analytics and insights",
      "17% discount compared to monthly",
    ],
  },
];

const SubscriptionPlans: React.FC<SubscriptionPlansProps> = ({ currentTier, subscribed }) => {
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(false);
  const { toast } = useToast();

  const handleRedeemCode = async () => {
    if (!promoCode.trim()) return;
    setLoadingPlan("redeem");
    try {
      const { data, error } = await supabase.functions.invoke("redeem-access-code", {
        body: { code: promoCode.trim() },
      });
      if (error) {
        // Surface the real reason from the function's JSON body
        let message = error.message;
        const ctx: any = (error as any).context;
        try {
          if (ctx && typeof ctx.json === "function") {
            const body = await ctx.json();
            if (body?.error) message = body.error;
          }
        } catch { /* keep default message */ }
        throw new Error(message);
      }
      if (data?.error) throw new Error(data.error);

      toast({
        title: data.already_redeemed ? "Already Redeemed" : "🎉 Access Granted!",
        description: data.already_redeemed
          ? `You've already redeemed this code. Access expires ${new Date(data.expires_at).toLocaleDateString()}.`
          : `You now have ${data.duration_days} days of free access! Expires ${new Date(data.expires_at).toLocaleDateString()}.`,
      });
      setPromoApplied(true);
      // Redirect to dashboard after short delay
      setTimeout(() => {
        window.location.href = "/dashboard";
      }, 2000);
    } catch (err: any) {
      toast({
        title: "Invalid Code",
        description: err.message || "That code is not valid. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleSelectPlan = async (planId: SubscriptionTier) => {
    setLoadingPlan(planId);
    try {
      const tier = SUBSCRIPTION_TIERS[planId];
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId: tier.price_id },
      });
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to start checkout. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <div className="space-y-8">
      {/* Promo code input */}
      <div className="max-w-md mx-auto">
        <div className="flex items-center gap-3 p-4 rounded-xl border border-border bg-card/50 backdrop-blur-xl">
          <Tag className="h-5 w-5 text-primary shrink-0" />
          <Input
            placeholder="Enter promotional code"
            value={promoCode}
            onChange={(e) => {
              setPromoCode(e.target.value);
              setPromoApplied(false);
            }}
            className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
          />
          {promoCode.trim() && (
            <Button
              variant="ghost"
              size="sm"
              className="shrink-0 text-primary"
              disabled={loadingPlan === "redeem"}
              onClick={handleRedeemCode}
            >
              {loadingPlan === "redeem" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Redeem"
              )}
            </Button>
          )}
        </div>
        {promoApplied && promoCode.trim() && (
          <p className="text-xs text-primary mt-2 text-center">
            ✨ Access granted! Redirecting to dashboard...
          </p>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-6">
      {plans.map((plan) => {
        const isCurrentPlan = subscribed && currentTier === plan.id;
        const isPopular = plan.popular;
        return (
          <Card
            key={plan.id}
            className={`relative overflow-hidden transition-all duration-300 hover:scale-[1.02] ${
              isPopular
                ? "border-primary/60 shadow-[0_0_30px_-5px_hsl(var(--primary)/0.3)] bg-card/80"
                : "border-border bg-card/50"
            } backdrop-blur-xl`}
          >
            {isPopular && (
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary to-transparent" />
            )}
            {isPopular && (
              <div className="absolute top-3 right-3 flex items-center gap-1 px-3 py-1 rounded-full bg-primary/20 border border-primary/30 text-xs font-medium text-primary-foreground">
                <Sparkles className="h-3 w-3" />
                Most Popular
              </div>
            )}
            {isCurrentPlan && (
              <div className="absolute top-3 left-3 flex items-center gap-1 px-3 py-1 rounded-full bg-accent border border-primary/30 text-xs font-medium text-accent-foreground">
                <Check className="h-3 w-3" />
                Your Plan
              </div>
            )}
            <CardHeader className="pt-8">
              <CardTitle className="text-xl text-foreground">{plan.name}</CardTitle>
              <div className="mt-3">
                <span className="text-4xl font-bold text-gradient">{plan.price}</span>
                <span className="text-muted-foreground ml-1 text-sm">/{plan.period.replace("per ", "").replace("every ", "")}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-3">{plan.description}</p>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start">
                    <div className="mt-0.5 mr-3 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/20">
                      <Check className="h-3 w-3 text-primary" />
                    </div>
                    <span className="text-sm text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
            <CardFooter className="pb-8">
              <Button
                className={`w-full ${
                  isPopular && !isCurrentPlan
                    ? "cosmic-button"
                    : ""
                }`}
                variant={isCurrentPlan ? "secondary" : isPopular ? "default" : "outline"}
                disabled={isCurrentPlan || loadingPlan === plan.id}
                onClick={() => handleSelectPlan(plan.id)}
              >
                {loadingPlan === plan.id ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing...</>
                ) : isCurrentPlan ? (
                  "Current Plan"
                ) : (
                  "Subscribe"
                )}
              </Button>
            </CardFooter>
          </Card>
        );
      })}
      </div>
    </div>
  );
};

export default SubscriptionPlans;
